// ═══════════════════════════════════════════════════════════════
// PublicRecommendations — the customer picks what they want done.
//
// Nothing is preselected. An undecided item is not a charge, and the
// total only moves when they say yes — so the number on screen is
// always what they'd actually owe, never a default they have to
// argue their way out of.
//
// Declining is a first-class button, not an unchecked box: "no" is a
// real answer worth recording, and it's what feeds the next visit's
// "you passed on this last time."
// ═══════════════════════════════════════════════════════════════
import React from 'react';
import { Check, X, Lightbulb, AlertTriangle } from 'lucide-react';
import { formatPrice } from './lib/job-helpers.js';
import { REC_ACCEPTED, REC_DECLINED } from './lib/recommendations.js';

export default function PublicRecommendations({
  recommendations, decisions, onDecide, locked = false,
}) {
  const list = Array.isArray(recommendations) ? recommendations : [];
  if (list.length === 0) return null;

  return (
    <div className="public-recs">
      <h3 className="public-recs-title">
        <Lightbulb size={16} /> Recommended work
      </h3>
      <p className="public-recs-help muted small">
        {locked
          ? 'Here’s what you chose.'
          : 'Your mechanic found these while checking the vehicle. Pick what you’d like done — the total updates as you choose.'}
      </p>

      <ul className="public-recs-list">
        {list.map((r) => {
          const decision = decisions?.[r.id] || null;
          const accepted = decision === REC_ACCEPTED;
          const declined = decision === REC_DECLINED;
          return (
            <li
              key={r.id}
              className={`public-rec ${r.urgency === 'replace' ? 'urgent' : ''} ${
                accepted ? 'accepted' : ''} ${declined ? 'declined' : ''}`}
            >
              <div className="public-rec-head">
                <span className="public-rec-label">
                  {r.urgency === 'replace' && (
                    <AlertTriangle size={13} className="public-rec-urgent-icon" />
                  )}
                  {r.label}
                </span>
                <span className="public-rec-price mono">{formatPrice(r.price)}</span>
              </div>

              {r.detail && <p className="public-rec-detail">{r.detail}</p>}

              {r.photoUrls?.length > 0 && (
                <div className="public-rec-photos">
                  {r.photoUrls.map((url, i) => (
                    <a key={url + i} href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt={`${r.label} photo ${i + 1}`} loading="lazy" />
                    </a>
                  ))}
                </div>
              )}

              {locked ? (
                <p className={`public-rec-verdict ${accepted ? 'yes' : declined ? 'no' : ''}`}>
                  {accepted && <><Check size={13} strokeWidth={3} /> You approved this</>}
                  {declined && <><X size={13} strokeWidth={3} /> You passed on this</>}
                  {!decision && <>No answer given</>}
                </p>
              ) : (
                <div className="public-rec-choice" role="group" aria-label={`${r.label} — yes or no`}>
                  <button
                    type="button"
                    className={`public-rec-btn yes${accepted ? ' active' : ''}`}
                    aria-pressed={accepted}
                    onClick={() => onDecide(r.id, accepted ? null : REC_ACCEPTED)}
                  >
                    <Check size={15} strokeWidth={3} /> Yes, do it
                  </button>
                  <button
                    type="button"
                    className={`public-rec-btn no${declined ? ' active' : ''}`}
                    aria-pressed={declined}
                    onClick={() => onDecide(r.id, declined ? null : REC_DECLINED)}
                  >
                    <X size={15} strokeWidth={3} /> Not now
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
